import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Tabs,
  Tab,
  Snackbar,
  Stack,
  CircularProgress,
  Container,
} from "@mui/material";
import {
  Search,
  FavoriteBorder,
  Favorite,
  Share,
  BookmarkBorder,
  Bookmark,
  Facebook,
  Twitter,
  Instagram,
  YouTube,
  LinkedIn,
  Email,
  Phone,
  LocationOn,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import PoliticsSection from "./PoliticsSection";
import {
  fetchHomePageData,
  fetchCategoryData,
  fetchTrendingTopics,
} from "../services/api";
import banner3 from "../asset/1.jpeg";
import BusinessSection from "./BusinessSection"; //
import SportsSection from "./SportsSection";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Link, Link as RouterLink } from "react-router-dom";

export default function Home() {
  const [mounted, setMounted] = useState(false);
  
  const categories = [
    "All",
    "Transport",
    "Politics",
    "Business",
    "Sports",
    "Technology",
  ];
  const perPage = 4;
  const moreStories = [
    {
      img: "https://placehold.co/600x400",
      title: "በአዲስ አበባ የትራንስፖርት ስርዓት ተመርቋል",
      date: "June 14, 2025",
      category: "Transport",
    },
    {
      img: "https://placehold.co/600x400",
      title: "የፖለቲካ ምክክር ተካሄደ",
      date: "June 13, 2025",
      category: "Politics",
    },
    {
      img: "https://placehold.co/600x400",
      title: "የብሄራዊ ቡድን ውጤት",
      date: "June 12, 2025",
      category: "Sports",
    },
    {
      img: "https://placehold.co/600x400",
      title: "የቴክኖሎጂ ፈተና",
      date: "June 11, 2025",
      category: "Technology",
    },
  ];
  // State for dynamic data
  const [data, setData] = useState({
    topBanners: [],
    featuredArticle: null,
    moreStories: [],
    latestNews: [],
    trendingTopics: [],
    categoryData: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI state
  const [tabValue, setTabValue] = useState(0);
  const [activeCat, setActiveCat] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(true);
  const [page, setPage] = useState(1);
  const [liked, setLiked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "" });

  // Handle mounting to prevent hydration issues
  useEffect(() => {
    setMounted(true);
  }, []);

  // Fetch initial data
  useEffect(() => {
    if (!mounted) return;
    
    const loadData = async () => {
      try {
        setLoading(true);
        const [homeData, trending] = await Promise.all([
          fetchHomePageData(),
          fetchTrendingTopics(),
        ]);

        setData({
          ...homeData,
          trendingTopics: trending,
          categoryData: {},
        });
      } catch (err) {
        setError(err.message);
        openSnack("Failed to load data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [mounted]);

  // Fetch category data when tab changes
  useEffect(() => {
    if (activeCat !== "All") {
      const loadCategoryData = async () => {
        try {
          setLoading(true);
          const categoryData = await fetchCategoryData(activeCat.toLowerCase());
          setData((prev) => ({
            ...prev,
            categoryData: {
              ...prev.categoryData,
              [activeCat]: categoryData,
            },
          }));
        } catch (err) {
          setError(err.message);
          openSnack(`Failed to load ${activeCat} data.`);
        } finally {
          setLoading(false);
        }
      };

      // Only fetch if we haven't already
      if (!data.categoryData[activeCat]) {
        loadCategoryData();
      }
    }
  }, [activeCat, data.categoryData]);

  // Handlers
  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
    setActiveCat(categories[newValue]);
    setSearchQuery("");
    setShowSearch(true);
    setPage(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setShowSearch(false);
    setPage(1);
  };

  const handleTrendingClick = (topic) => (e) => {
    e.preventDefault();
    setSearchQuery(topic.title);
    setShowSearch(false);
    setPage(1);
  };

  const openSnack = (msg) => setSnack({ open: true, msg });

  // Filtering & paging
  const storiesToDisplay =
    activeCat === "All"
      ? data.moreStories
      : data.categoryData[activeCat]?.stories || [];

  const filteredStories = storiesToDisplay.filter((s) =>
    s.title.toLowerCase().includes(searchQuery.trim().toLowerCase())
  );

  const pagedStories = filteredStories.slice(0, page * perPage);

  const newsToDisplay =
    activeCat === "All"
      ? data.latestNews
      : data.categoryData[activeCat]?.news || [];

  // Prevent hydration issues by not rendering until mounted
  if (!mounted) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", mt: 10 }}>
        <Typography color="error">{error}</Typography>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 15 }}>
      {/* Top Banners */}
      <Grid container spacing={2} sx={{ mb: 3, mt: 10 }}>
        {data.topBanners.slice(0, 2).map((banner, i) => (
                          <Grid xs={6} sm={6} md={6} key={i}>
            <motion.div
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <Card square elevation={1}>
                <CardMedia
                  component="img"
                  height={150}
                  image={banner.imageUrl}
                  alt={banner.altText}
                />
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ px: 2, py: 1 }}>
        {/* Category Tabs */}
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2 }}
        >
          {categories.map((cat) => (
            <Tab label={cat} key={cat} />
          ))}
        </Tabs>

        {/* Search */}
        {showSearch && (
          <Box
            component="form"
            onSubmit={handleSearch}
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <TextField
              size="small"
              placeholder="Search stories…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                endAdornment: (
                  <IconButton type="submit">
                    <Search />
                  </IconButton>
                ),
              }}
            />
          </Box>
        )}

        {/* Featured + Sidebar */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
            mb: 4,
          }}
        >
          {/* Featured Article */}
          {data.featuredArticle && (
            <Box sx={{ flex: 3 }}>
              <motion.div
                whileHover={{ scale: 1.01 }}
                transition={{ duration: 0.2 }}
              >
                <Card square elevation={2}>
                  <Box>
                    <Box sx={{ position: "relative" }}>
                      <CardMedia
                        component="img"
                        height={300}
                        image={data.featuredArticle.imageUrl}
                        alt="Featured"
                      />
                      <Chip
                        label={data.featuredArticle.category}
                        color="primary"
                        size="small"
                        sx={{ position: "absolute", top: 16, left: 16 }}
                      />
                    </Box>
                    <CardContent>
                      <Typography
                        variant="h5"
                        gutterBottom
                        sx={{ fontFamily: "serif", fontWeight: "bold" }}
                      >
                        {data.featuredArticle.title}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                        <Typography variant="caption">
                          {new Date(
                            data.featuredArticle.date
                          ).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption">
                          {data.featuredArticle.views} views
                        </Typography>
                        <Typography variant="caption">
                          {data.featuredArticle.comments} comments
                        </Typography>
                      </Box>
                      <Typography paragraph color="text.secondary">
                        {data.featuredArticle.excerpt}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setLiked(!liked);
                            }}
                          >
                            {liked ? (
                              <Favorite color="error" />
                            ) : (
                              <FavoriteBorder />
                            )}
                          </IconButton>
                          <IconButton onClick={(e) => e.stopPropagation()}>
                            <Share />
                          </IconButton>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setBookmarked(!bookmarked);
                            }}
                          >
                            {bookmarked ? (
                              <Bookmark color="primary" />
                            ) : (
                              <BookmarkBorder />
                            )}
                          </IconButton>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          component={Link}
                          to={`/articles/${data.featuredArticle._id}`}
                        >
                          Read More
                        </Button>
                      </Box>
                    </CardContent>
                  </Box>
                </Card>
              </motion.div>
            </Box>
          )}

          {/* Sidebar */}
          <Box sx={{ flex: 1 }}>
            <Card square elevation={2} sx={{ mb: 3 }}>
              <CardMedia
                component="img"
                image="https://i.ibb.co/KCD6Zvk/reporter-website-2-299x359.png"
                alt="Ad"
              />
            </Card>
            <Card square elevation={2} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Trending Now
              </Typography>
              {data.trendingTopics.map((topic, i) => (
                <Link
                  key={i}
                  href="#"
                  underline="none"
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1,
                  }}
                  onClick={handleTrendingClick(topic)}
                >
                  <Typography>{topic.title}</Typography>
                  <Typography variant="caption">{topic.posts}</Typography>
                </Link>
              ))}
            </Card>
          </Box>
        </Box>

        {/* More Stories */}
        <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
          More Stories
        </Typography>
        <Box sx={{ width: "100%", overflow: "hidden" }}>
          <Grid
            container
            spacing={0.5}
            alignItems="stretch"
            sx={{
              mb: 4,
              flexWrap: "nowrap",
              overflowX: "auto",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {pagedStories.map((story, i) => (
              <Grid
                item
                key={i}
                sx={{
                  display: "flex",
                  minWidth: 0,
                  flex: {
                    xs: "0 0 calc(90% - 16px)",
                    sm: "0 0 calc(50% - 16px)",
                    md: "0 0 calc(33.33% - 16px)",
                    lg: "0 0 calc(25% - 16px)",
                  },
                  px: 1,
                }}
              >
                <motion.div
                  whileHover={{
                    translateY: -5,
                    boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
                  }}
                  transition={{ duration: 0.2 }}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    width: "100%",
                  }}
                >
                  {/* Add Link component here */}
                  <Link
                    to={`/articles/${story._id}`} // or any unique identifier
                    style={{ textDecoration: "none" }}
                  >
                    <Card
                      square
                      elevation={1}
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        height: {
                          xs: 250,
                          sm: 300,
                        },
                        width: "100%",
                        overflow: "hidden",
                        cursor: "pointer", // Add pointer cursor
                      }}
                    >
                      {/* Image Section */}
                      <Box
                        sx={{
                          height: {
                            xs: 120,
                            sm: 140,
                          },
                          overflow: "hidden",
                        }}
                      >
                        <CardMedia
                          component="img"
                          src={story.img}
                          alt={story.title}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </Box>

                      {/* Content Section */}
                      <CardContent
                        sx={{
                          flexGrow: 1,
                          display: "flex",
                          flexDirection: "column",
                          p: {
                            xs: 1,
                            sm: 1.5,
                          },
                          "&:last-child": {
                            pb: {
                              xs: 1,
                              sm: 1.5,
                            },
                          },
                          overflow: "hidden",
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Chip
                            label={story.category}
                            size="small"
                            color="secondary"
                            sx={{
                              mb: 1,
                              maxWidth: "100%",
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                            }}
                          />

                          <Typography
                            variant="subtitle1"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              mb: 1,
                              wordBreak: "break-word",
                              fontSize: {
                                xs: "0.875rem",
                                sm: "1rem",
                              },
                            }}
                          >
                            {story.title}
                          </Typography>
                        </Box>

                        <Box sx={{ mt: "auto" }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "block",
                            }}
                          >
                            {new Date(story.date).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Box>

        {filteredStories.length > pagedStories.length && (
          <Box textAlign="center" mb={4}>
            <Button onClick={() => setPage((p) => p + 1)}>Load More</Button>
          </Box>
        )}

        {/* Latest News */}
        <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
          Latest News
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
            width: "100%",
          }}
        >
          {/* Main News Content - Fixed width container */}
          <Box
            sx={{
              flex: 3,
              width: { xs: "100%", md: "calc(100% - 336px)" }, // Adjust based on sidebar width
            }}
          >
            <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
              {newsToDisplay.map((news, i) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  key={i}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Card
                    square
                    elevation={1}
                    sx={{
                      width: "100%",
                      maxWidth: "350px", // Fixed maximum width
                      height: "400px", // Fixed height
                      display: "flex",
                      flexDirection: "column",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 3,
                      },
                      padding: "10px",
                    }}
                  >
                    <CardActionArea
                      component={Link}
                      to={`/articles/${news._id}`}
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                      }}
                    >
                      {/* Fixed height image */}
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          height: "200px",
                          overflow: "hidden",
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={news.img}
                          alt={news.title}
                          sx={{
                            width: "350px",
                            height: "100%",
                            objectFit: "cover",
                            transition: "transform 0.3s",
                            "&:hover": {
                              transform: "scale(1.05)",
                            },
                          }}
                        />
                        {news.isBreaking && (
                          <Chip
                            label="BREAKING"
                            color="error"
                            size="small"
                            sx={{
                              position: "absolute",
                              top: 8,
                              left: 8,
                              fontWeight: "bold",
                            }}
                          />
                        )}
                      </Box>

                      {/* Fixed height content */}
                      <CardContent
                        sx={{
                          height: "300px",
                          display: "flex",
                          flexDirection: "column",
                          p: { xs: 1.5, sm: 2 },
                        }}
                      >
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{
                            width: "350px",
                            fontFamily: "serif",
                            fontWeight: "bold",
                            fontSize: "1.1rem",
                            lineHeight: 1.3,
                          }}
                        >
                          {news.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontStyle: "italic", mb: 1.5 }}
                        >
                          {news.time}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            flexGrow: 1,
                            display: "-webkit-box",
                            WebkitLineClamp: 4,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            mb: 2,
                            lineHeight: 1.5,
                          }}
                        >
                          {news.excerpt}
                        </Typography>
                        <Box sx={{ textAlign: "right", mt: "auto" }}>
                          <Typography
                            variant="body2"
                            color="primary"
                            sx={{
                              cursor: "pointer",
                              fontWeight: "bold",
                              "&:hover": {
                                textDecoration: "underline",
                              },
                            }}
                          >
                            Read More
                          </Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Sidebar - Fixed width */}
          <Box
            sx={{
              width: { xs: "100%", md: "300px" },
              flexShrink: 0,
            }}
          >
            <Card square elevation={2} sx={{ mb: 3, p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Trending Videos
              </Typography>
              {[
                {
                  id: 1,
                  title: "አብይ የአሰብን ግዙፍ የኮሪደር ፕሮጀክት ይፋ አደረጉ | የአያቶላ አስደንጋጩ ዛቻ",
                  thumbnail: "https://i.ibb.co/Wp7qXXb9/aa-41.jpg",
                  views: "1.2k",
                  link: "https://www.youtube.com/watch?v=b1_oLXu1dcI&t=198s",
                },
                {
                  id: 2,
                  title: "Learn Material UI Complete Course",
                  thumbnail:
                    "https://i.ibb.co/gL8VKRD0/aa-41.jpg",
                  views: "1K",
                  link: "https://www.youtube.com/watch?v=0CAE1mo4GWM",
                },
                {
                  id: 3,
                  title: "JavaScript Crash Course For Beginners",
                  thumbnail:
                    "https://i.ibb.co/nqjGKVCY/a-22.jpg",
                  views: "3.7M",
                  link: "https://www.youtube.com/watch?v=hdI2bqOjy3c",
                },
              ].map((video) => (
                <Box key={video.id} sx={{ mb: 2 }}>
                  <Box
                    component="a"
                    href={video.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      position: "relative",
                      width: "100%",
                      height: "160px",
                      overflow: "hidden",
                      mb: 1,
                      borderRadius: 1,
                      display: "block",
                      textDecoration: "none",
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={video.thumbnail}
                      alt={`${video.title} thumbnail`}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition: "transform 0.3s",
                        "&:hover": {
                          transform: "scale(1.05)",
                        },
                      }}
                    />
                  </Box>
                  <Typography
                    component="a"
                    href={video.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="subtitle2"
                    sx={{
                      display: "block",
                      textDecoration: "none",
                      color: "inherit",
                      "&:hover": {
                        color: "primary.main",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {video.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {video.views} views
                  </Typography>
                </Box>
              ))}
            </Card>
          </Box>
        </Box>
        {/* Mid-Page Ad */}
        <Card
          square
          elevation={0}
          sx={{
            textAlign: "center",
            mb: 4,
            p: 2,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="h6" gutterBottom>
            Advertisement
          </Typography>
          <CardMedia
            component="img"
            height={90}
            image={banner3}
            alt="Sponsor"
            sx={{ mx: "auto", maxWidth: 600 }}
          />
        </Card>
        {/* Category Sections with Posts */}
        <Box sx={{ mb: 4 }}>
          
          {/* Politics Category */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 3 }}>
              Politics
            </Typography>
            <PoliticsSection />
          </Box>
          <hr></hr>
          {/* Business Category */}

          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 3 }}>
              Business
            </Typography>
            <BusinessSection />
          </Box>
          <hr></hr>
          {/* Sports Category */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 3 }}>
              Sports
            </Typography>
            <SportsSection />
          </Box>
        </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 4,
          px: 2,
          backgroundColor: "background.paper",
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Quick Links */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Quick Links
              </Typography>
                             <Stack spacing={1}>
                 {[
                   { name: "Home", path: "/" },
                   { name: "About Us", path: "/about" },
                   { name: "Contact", path: "/contact" }
                 ].map((item) => (
                   <motion.div whileHover={{ x: 5 }} key={item.name}>
                     <Link
                       to={item.path}
                       style={{
                         display: "block",
                         color: "inherit",
                         textDecoration: "none",
                       }}
                       sx={{
                         color: "text.secondary",
                         "&:hover": { color: "primary.main" },
                       }}
                     >
                       {item.name}
                     </Link>
                   </motion.div>
                 ))}
              </Stack>
            </Grid>

            {/* Categories */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Categories
              </Typography>
              <Stack spacing={1}>
                {["Politics", "Business", "Sports"].map((category) => (
                  <motion.div whileHover={{ x: 5 }} key={category}>
                    <Link
                      component={RouterLink}
                      to={`/${category.toLowerCase()}-news`}
                      sx={{
                        display: "block",
                        color: "text.secondary",
                        textDecoration: "none",
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      {category}
                    </Link>
                  </motion.div>
                ))}
              </Stack>
            </Grid>

            {/* Contact Info */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Contact Us
              </Typography>
              <Stack spacing={1.5}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <LocationOn color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    123 News Street, Addis Ababa, Ethiopia
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Phone color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">+251 123 456 789</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Email color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">info@ethioreview.com</Typography>
                </Box>
              </Stack>
            </Grid>

            {/* Social Media & Newsletter */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Follow Us
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                {[
                  { icon: <Facebook />, color: "#3b5998", name: "Facebook" },
                  { icon: <Twitter />, color: "#1DA1F2", name: "Twitter" },
                  { icon: <Instagram />, color: "#E1306C", name: "Instagram" },
                  { icon: <YouTube />, color: "#FF0000", name: "YouTube" },
                  { icon: <LinkedIn />, color: "#0077B5", name: "LinkedIn" },
                ].map((social) => (
                  <motion.div whileHover={{ scale: 1.1 }} key={social.name}>
                    <IconButton
                      href="#"
                      target="_blank"
                      rel="noopener"
                      sx={{
                        backgroundColor: `${social.color}10`,
                        color: social.color,
                        "&:hover": { backgroundColor: `${social.color}20` },
                      }}
                    >
                      {social.icon}
                    </IconButton>
                  </motion.div>
                ))}
              </Stack>

              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Newsletter
              </Typography>
              <Box component="form" sx={{ display: "flex" }}>
                <TextField
                  size="small"
                  placeholder="Your email"
                  variant="outlined"
                  fullWidth
                  sx={{ mr: 1 }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Subscribe
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* Copyright & Legal */}
          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: 1,
              borderColor: "divider",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} EthioReview. All rights reserved.
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mt: { xs: 2, sm: 0 } }}>
              {[
                { name: "Privacy Policy", path: "/privacy" },
                { name: "Terms of Service", path: "/terms" },
                { name: "Cookie Policy", path: "/cookies" }
              ].map((item) => (
                <Typography
                  key={item.name}
                  component={Link}
                  to={item.path}
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    textDecoration: "none",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  {item.name}
                </Typography>
              ))}
            </Stack>
          </Box>
        </Container>
      </Box>

        {/* Snackbar */}
        <Snackbar
          open={snack.open}
          autoHideDuration={2000}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          message={snack.msg}
        />
      </Box>
    </Box>
  );
}
